import {ComponentType, FC, ReactNode, useMemo} from 'react';

import {createView, view, type View} from '@sail/ui';

import {
  Conversation,
  Observability,
} from '@stripe-internal/conversations-client';
import {InlineDataComponentRegistry} from '@stripe-internal/inline-data';
import type {MessagePartComponentRegistry} from '@stripe-internal/conversations-ui';
import {type Theme} from '@stripe-internal/conversations-ui';

import {AppCloseHandler, AppConfig, AppProfile} from '../core/types';
import {AppLiveChatIframe} from '../core/components/AppLiveChatIframe';
import {AssistantContainer} from '../core/components/AssistantContainer';
import {AssistantLog} from '../core/components/AssistantLog';
import {AssistantScreen} from '../core/components/AssistantScreen';
import {DefaultErrorBoundary} from '../core/components/ErrorBoundary';
import {ParentMessageBus} from '../core/lib/parentMessageBus/ParentMessageBus';
import {StripeAssistantDisclaimer} from './components/Footer/StripeAssistantDisclaimer';
import {StripeAssistantHeader} from './components/Header/StripeAssistantHeader';
import {StripeAssistantProviders} from './providers/StripeAssistantProviders';
import {StripeAssistantSubheaderDisclaimer} from './components/Subheader/StripeAssistantSubheaderDisclaimer';
import {useVisibility} from '../core/hooks/appHooks';

type Props = View.IntrinsicElement<
  'div',
  {
    config: AppConfig;
    profile: AppProfile;
    withLiveChat?: boolean;
    clientFetch?: typeof fetch;
    parentMessageBus?: ParentMessageBus;
    header?: ReactNode | ComponentType;
    subheader?: ReactNode | ComponentType;
    footer?: ReactNode | ComponentType;
    showWave?: boolean;
    log?: ReactNode | ComponentType<{conversation: Conversation}>;
    input?: ReactNode | ComponentType<{conversation: Conversation}>;
    inlineDataComponents?: InlineDataComponentRegistry;
    messagePartComponents?: MessagePartComponentRegistry;
    errorBoundary?: ComponentType<{children: ReactNode}> | null;
    onClose?: AppCloseHandler;
    observability?: Observability;
    theme?: Theme;
    themeOverrides?: Partial<Theme>;
  }
>;

function DefaultStripeAssistantLog({
  conversation,
}: {
  conversation: Conversation;
}) {
  return (
    <AssistantLog
      conversation={conversation}
      topContent={<StripeAssistantSubheaderDisclaimer />}
    />
  );
}

function AssistantScreenWhenOpen({
  log,
  input,
}: {
  log?: ReactNode | ComponentType<{conversation: Conversation}>;
  input?: ReactNode | ComponentType<{conversation: Conversation}>;
}) {
  const {visibility} = useVisibility();

  if (visibility === 'closed') {
    return null;
  }

  return (
    <AssistantScreen
      log={log === undefined ? DefaultStripeAssistantLog : log}
      input={input}
    />
  );
}

export const StripeAssistant: FC<View.ViewProps<Props>> = createView<Props>(
  ({
    config,
    profile,
    withLiveChat = false,
    clientFetch,
    parentMessageBus,
    header = StripeAssistantHeader,
    subheader,
    footer = StripeAssistantDisclaimer,
    showWave = true,
    log,
    input,
    inlineDataComponents,
    messagePartComponents,
    errorBoundary,
    onClose,
    observability,
    theme,
    themeOverrides,
    ...props
  }) => {
    const defaultErrorBoundary = useMemo(() => {
      return ({children}: {children: ReactNode}) => (
        <DefaultErrorBoundary config={config}>{children}</DefaultErrorBoundary>
      );
    }, [config]);

    // not using ?? as we want to allow for overriding with null
    const errorBoundaryOrDefault =
      errorBoundary === undefined ? defaultErrorBoundary : errorBoundary;

    return (
      <view.div
        data-testid="stripe-assistant-root"
        css={{height: 'fill', overflow: 'hidden'}}
        {...props}
      >
        <StripeAssistantProviders
          config={config}
          profile={profile}
          clientFetch={clientFetch}
          parentMessageBus={parentMessageBus}
          inlineDataComponents={inlineDataComponents}
          messagePartComponents={messagePartComponents}
          onClose={onClose}
          observability={observability}
          theme={theme}
          themeOverrides={themeOverrides}
        >
          <AssistantContainer
            header={header}
            subheader={subheader}
            footer={footer}
            errorBoundary={errorBoundaryOrDefault}
            showWave={showWave}
          >
            <AssistantScreenWhenOpen log={log} input={input} />
            {withLiveChat ? <AppLiveChatIframe /> : null}
          </AssistantContainer>
        </StripeAssistantProviders>
      </view.div>
    );
  },
);
